import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import moment from 'moment';

// Icons
import {
    ArrowDownLeft,
    ArrowLeft,
    Ban,
    BadgeCheck,
    Building2,
    CalendarClock,
    ChevronDown,
    Download,
    Eye,
    FileWarning,
    LayoutDashboard,
    Lock,
    Pencil,
    Plus,
    ReceiptText,
    ShieldAlert,
    ShoppingBag,
    Square,
    StickyNote,
    Timer,
    Trash2,
    TrendingUp,
    Unlock,
    Users,
    Wallet,
} from 'lucide-react';

// Components
import Loading from '../components/Loading';
import {
    Avatar,
    Badge,
    Button,
    Card,
    CardHeader,
    DataTable,
    EmptyState,
    ErrorState,
    IconButton,
    IconChip,
    KeyValue,
    Modal,
    Money,
    Notice,
    Page,
    PersonCell,
    SelectInput,
    StatCard,
    StatGrid,
    StatusPill,
    Tabs,
    TableSkeleton,
    TextInput,
    Textarea,
} from '../ui';

// Redux Actions
import {
    getPlayerProfile,
    getPlayerFinancialSummary,
    getPlayerTransactions,
    getPlayerAnalytics,
    getPlayerClubs,
    getPlayerReferrals,
    freezePlayerAccount,
    banPlayerAccount,
    unbanPlayerAccount,
    getPlayerFullPhone,
    assignPlayerClub,
    removePlayerClub,
    getPlayerRemarks,
    updatePlayerRemarks,
} from '../actions/playerActions';

import {
    startPlayerSession,
    endPlayerSession,
    getActivePlayerSession,
    getPlayerSessionSummary,
} from '../actions/sessionActions';

import { listClubs } from '../actions/clubActions';
import { handleUploadImageError, resolveUploadUrl } from '../utils/uploadUrl';

// Manual Affiliate Actions
import {
    getPlayerSessionHours,
    logSessionHours,
} from '../actions/affiliateActions';

// Constants
import {
    GET_PLAYER_PROFILE_RESET,
    GET_PLAYER_SUMMARY_RESET,
    GET_PLAYER_TRANSACTIONS_RESET,
    GET_PLAYER_ANALYTICS_RESET,
    GET_PLAYER_CLUBS_RESET,
    GET_PLAYER_REFERRALS_RESET,
    FREEZE_PLAYER_RESET,
    BAN_PLAYER_RESET,
    UNBAN_PLAYER_RESET,
    GET_PLAYER_PHONE_RESET,
    ASSIGN_PLAYER_CLUB_RESET,
    REMOVE_PLAYER_CLUB_RESET,
    GET_PLAYER_REMARKS_RESET,
    UPDATE_PLAYER_REMARKS_RESET,
} from '../constants/playerConstants';

import {
    START_SESSION_RESET,
    END_SESSION_RESET,
    GET_ACTIVE_SESSION_RESET,
} from '../constants/sessionConstants';

import {
    GET_PLAYER_AFFILIATES_RESET,
    GET_PLAYER_SESSION_HOURS_RESET,
    LOG_SESSION_HOURS_RESET,
    GET_POTENTIAL_AFFILIATES_RESET,
} from '../constants/affiliateConstants';
import { useDialog } from '../components/DialogProvider';
import { displayTransactionId } from '../utils/transactionIdUtils';

// ============================================================================
// HELPER: Format currency
// ============================================================================
const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return '₹0';
    return new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency: 'INR',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    }).format(amount);
};

// ============================================================================
// HELPER: Format date
// ============================================================================
const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return moment(dateString).format('DD MMM YYYY, hh:mm A');
};

// ============================================================================
// HELPER: Back link
// ============================================================================
const BackLink = ({ onClick }) => (
    <div>
        <button
            type="button"
            onClick={onClick}
            className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-cm-muted hover:text-cm-text"
        >
            <ArrowLeft size={15} aria-hidden="true" />
            Back
        </button>
    </div>
);

// ============================================================================
// HELPER: Transaction type badge
// ============================================================================
const TRANSACTION_TYPES = {
    deposit: { tone: 'info', label: 'Top-up' },
    'chip-deposit': { tone: 'info', label: 'Chip Deposit' },
    'buy-in': { tone: 'gold', label: 'Buy-In' },
    'deposit-buyin': { tone: 'gold', label: 'Top-up Buy-In' },
    'table-charge': { tone: 'danger', label: 'Table Charge' },
    'food-drink': { tone: 'danger', label: 'Food & Drink' },
    membership: { tone: 'danger', label: 'Membership' },
    'event-entry': { tone: 'danger', label: 'Event Entry' },
    cashout: { tone: 'success', label: 'Cashout' },
};

const TransactionTypeBadge = ({ type }) => {
    const config = TRANSACTION_TYPES[type] || { tone: 'neutral', label: type || '—' };
    return <Badge tone={config.tone}>{config.label}</Badge>;
};

// ============================================================================
// MAIN COMPONENT: PlayerProfile
// ============================================================================
const PlayerProfile = () => {
    const { confirm } = useDialog();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { id } = useParams();
    const playerId = id?.replace(':', '');

    // Local state
    const [activeTab, setActiveTab] = useState('overview');
    const [transactionPage, setTransactionPage] = useState(1);
    const [freezeModalOpen, setFreezeModalOpen] = useState(false);
    const [freezeReason, setFreezeReason] = useState('');
    const [revealedPhone, setRevealedPhone] = useState(null);

    // Ban/Unban state
    const [banModalOpen, setBanModalOpen] = useState(false);
    const [banCooldownDays, setBanCooldownDays] = useState(7);
    const [banRemarks, setBanRemarks] = useState('');
    const [unbanModalOpen, setUnbanModalOpen] = useState(false);
    const [unbanReason, setUnbanReason] = useState('');

    // Club assignment
    const [assignClubModalOpen, setAssignClubModalOpen] = useState(false);
    const [selectedClubsToAssign, setSelectedClubsToAssign] = useState([]); // Changed to array for multiple selection

    // Session management local state
    const [sessionModalOpen, setSessionModalOpen] = useState(false);
    const [selectedClubForSession, setSelectedClubForSession] = useState('');
    const [elapsedTime, setElapsedTime] = useState(0); // in seconds
    const [sessionTimerInterval, setSessionTimerInterval] = useState(null);

    // Manual Affiliate System local state
    const [sessionHoursModalOpen, setSessionHoursModalOpen] = useState(false);
    const [sessionHoursData, setSessionHoursData] = useState({
        clubId: '',
        sessionDate: new Date().toISOString().split('T')[0],
        hoursPlayed: '',
        remarks: ''
    });

    // Admin Remarks state
    const [remarksText, setRemarksText] = useState('');
    const [remarksCategory, setRemarksCategory] = useState('general');
    const [remarksEditMode, setRemarksEditMode] = useState(false);
    const [showRemarksHistory, setShowRemarksHistory] = useState(false);

    // Redux state
    const { loading: loadingProfile, player, error: errorProfile } = useSelector(
        (state) => state.playerProfile
    );
    const { loading: loadingSummary, summary } = useSelector(
        (state) => state.playerSummary
    );
    const { loading: loadingTransactions, transactions, pagination } = useSelector(
        (state) => state.playerTransactions
    );
    const { loading: loadingAnalytics, analytics } = useSelector(
        (state) => state.playerAnalytics
    );
    const { loading: loadingClubs, clubs } = useSelector(
        (state) => state.playerClubs
    );
    const { loading: loadingFreeze, success: freezeSuccess } = useSelector(
        (state) => state.freezePlayer
    );
    const userInfo = useSelector(
        (state) => state.userLogin?.userInfo || state.adminLogin?.userInfo
    );

    // Club assignment Redux state
    const { loading: loadingAssignClub, success: successAssignClub, error: errorAssignClub } = useSelector(
        (state) => state.assignPlayerClub
    );
    const { loading: loadingRemoveClub, success: successRemoveClub, error: errorRemoveClub } = useSelector(
        (state) => state.removePlayerClub
    );

    // Session management Redux state
    const { loading: loadingStartSession, success: startSessionSuccess } = useSelector(
        (state) => state.startSession
    );
    const { loading: loadingEndSession, success: endSessionSuccess } = useSelector(
        (state) => state.endSession
    );
    const { loading: loadingActiveSession, activeSession } = useSelector(
        (state) => state.activeSession
    );
    const { loading: loadingReferrals, referrals = [], bonusGrantedCount = 0 } = useSelector(
        (state) => state.playerReferrals
    );
    const { clubs: allClubs = [] } = useSelector((state) => state.clubList || {});

    // Manual Affiliate Redux state
    const { loading: loadingLogSessionHours, success: successLogSessionHours } = useSelector(
        (state) => state.logSessionHours || {}
    );
    const { manualHours, automaticHours } = useSelector(
        (state) => state.playerSessionHours || {}
    );

    // Admin Remarks Redux state
    const { loading: loadingRemarks, remarks: playerRemarks } = useSelector(
        (state) => state.playerRemarks || {}
    );
    const { loading: loadingUpdateRemarks, success: successUpdateRemarks, error: errorUpdateRemarks } = useSelector(
        (state) => state.updatePlayerRemarks || {}
    );

    // ============================================================================
    // EFFECTS
    // ============================================================================

    // Load player profile on mount
    useEffect(() => {
        if (playerId) {
            dispatch(getPlayerProfile(playerId));
            dispatch(getPlayerFinancialSummary(playerId));
            dispatch(getPlayerClubs(playerId));
            dispatch(getPlayerAnalytics(playerId));
            dispatch(getPlayerTransactions(playerId, 1, 10));
        }

        // Cleanup on unmount
        return () => {
            dispatch({ type: GET_PLAYER_CLUBS_RESET });
            dispatch({ type: GET_PLAYER_REFERRALS_RESET });
            dispatch({ type: FREEZE_PLAYER_RESET });
            dispatch({ type: GET_PLAYER_PHONE_RESET });
            // Session resets
            dispatch({ type: START_SESSION_RESET });
            dispatch({ type: END_SESSION_RESET });
            dispatch({ type: GET_ACTIVE_SESSION_RESET });
            if (sessionTimerInterval) clearInterval(sessionTimerInterval);
        };
    }, [dispatch, playerId, sessionTimerInterval]);

    // Load active session and clubs on mount
    useEffect(() => {
        if (playerId) {
            dispatch(getActivePlayerSession(playerId));
            dispatch(listClubs());
        }
    }, [dispatch, playerId]);

    // Handle session timer
    useEffect(() => {
        if (activeSession && activeSession.sessionStart) {
            const start = new Date(activeSession.sessionStart).getTime();

            // Should be immediate update
            const updateTimer = () => {
                const now = Date.now();
                const diffIsSeconds = Math.floor((now - start) / 1000);
                setElapsedTime(diffIsSeconds);
            };

            updateTimer(); // Initial call
            const interval = setInterval(updateTimer, 1000);
            setSessionTimerInterval(interval);

            return () => clearInterval(interval);
        } else {
            setElapsedTime(0);
        }
    }, [activeSession]);

    // Handle start session success
    useEffect(() => {
        if (startSessionSuccess) {
            toast.success('Club session started successfully');
            setSessionModalOpen(false);
            dispatch(getActivePlayerSession(playerId));
            dispatch({ type: START_SESSION_RESET });
            // Refresh summary
            dispatch(getPlayerFinancialSummary(playerId));
            // Note: getPlayerFinancialSummary likely doesn't include session stats, 
            // relying on getActivePlayerSession for now. 
            // If getPlayerProfile includes session stats, refresh that too:
            dispatch(getPlayerProfile(playerId));
        }
    }, [startSessionSuccess, dispatch, playerId]);

    // Handle end session success
    useEffect(() => {
        if (endSessionSuccess) {
            toast.success('Club session ended successfully');
            dispatch(getActivePlayerSession(playerId));
            dispatch({ type: END_SESSION_RESET });
            // Refresh profile to update stats
            dispatch(getPlayerProfile(playerId));
        }
    }, [endSessionSuccess, dispatch, playerId]);

    // Handle profile error
    useEffect(() => {
        if (errorProfile) {
            toast.error(errorProfile);
            if (errorProfile.includes('admin') || errorProfile.includes('staff')) {
                navigate('/users'); // Redirect to admin users page
            }
        }
    }, [errorProfile, navigate]);

    // Handle freeze success
    useEffect(() => {
        if (freezeSuccess) {
            toast.success('Player account status updated');
            setFreezeModalOpen(false);
            setFreezeReason('');
            dispatch(getPlayerProfile(playerId));
            dispatch({ type: FREEZE_PLAYER_RESET });
        }
    }, [freezeSuccess, dispatch, playerId]);

    // Load remarks when player profile loads
    useEffect(() => {
        if (playerId && player) {
            dispatch(getPlayerRemarks(playerId));
        }
    }, [dispatch, playerId, player]);

    // Update local remarks state when fetched from server
    useEffect(() => {
        if (playerRemarks?.current) {
            setRemarksText(playerRemarks.current.text || '');
            setRemarksCategory(playerRemarks.current.category || 'general');
        }
    }, [playerRemarks]);

    // Handle remarks update success
    useEffect(() => {
        if (successUpdateRemarks) {
            toast.success('Remarks updated successfully');
            setRemarksEditMode(false);
            dispatch({ type: UPDATE_PLAYER_REMARKS_RESET });
            dispatch(getPlayerRemarks(playerId));
        }
        if (errorUpdateRemarks) {
            toast.error(errorUpdateRemarks);
            dispatch({ type: UPDATE_PLAYER_REMARKS_RESET });
        }
    }, [successUpdateRemarks, errorUpdateRemarks, dispatch, playerId]);

    // Handle club assignment/removal success
    useEffect(() => {
        if (successAssignClub) {
            toast.success('Club(s) assigned successfully');
            setAssignClubModalOpen(false);
            setSelectedClubsToAssign([]);
            dispatch({ type: ASSIGN_PLAYER_CLUB_RESET });
            dispatch(getPlayerClubs(playerId));
        }
        if (successRemoveClub) {
            toast.success('Club removed successfully');
            dispatch({ type: REMOVE_PLAYER_CLUB_RESET });
            dispatch(getPlayerClubs(playerId));
        }
        if (errorAssignClub) {
            toast.error(errorAssignClub);
            dispatch({ type: ASSIGN_PLAYER_CLUB_RESET });
        }
        if (errorRemoveClub) {
            toast.error(errorRemoveClub);
            dispatch({ type: REMOVE_PLAYER_CLUB_RESET });
        }
    }, [successAssignClub, successRemoveClub, errorAssignClub, errorRemoveClub, dispatch, playerId]);

    // Load player affiliates when tab is active
    useEffect(() => {
        if (playerId && activeTab === 'affiliates') {
            dispatch(getPlayerSessionHours(playerId));
            dispatch(getPlayerReferrals(playerId));
        }
    }, [dispatch, playerId, activeTab]);

    // Handle session hours log success
    useEffect(() => {
        if (successLogSessionHours) {
            toast.success('Session hours logged successfully');
            setSessionHoursModalOpen(false);
            setSessionHoursData({
                clubId: '',
                sessionDate: new Date().toISOString().split('T')[0],
                hoursPlayed: '',
                remarks: ''
            });
            dispatch({ type: LOG_SESSION_HOURS_RESET });
            dispatch(getPlayerSessionHours(playerId));
        }
    }, [successLogSessionHours, dispatch, playerId]);

    // ============================================================================
    // HANDLERS
    // ============================================================================

    const handleFreezeToggle = async () => {
        try {
            const shouldFreeze = player?.status === true;
            await dispatch(freezePlayerAccount(playerId, shouldFreeze, freezeReason));
        } catch (error) {
            toast.error(error.message || 'Failed to update account status');
        }
    };

    const handleBanUser = async () => {
        try {
            await dispatch(banPlayerAccount(playerId, banCooldownDays, banRemarks));
            toast.success(`User banned for ${banCooldownDays} day(s)`);
            setBanModalOpen(false);
            setBanRemarks('');
            setBanCooldownDays(7);
            dispatch(getPlayerProfile(playerId)); // Refresh profile
        } catch (error) {
            toast.error(error.message || 'Failed to ban user');
        }
    };

    const handleUnbanUser = async () => {
        try {
            await dispatch(unbanPlayerAccount(playerId, unbanReason));
            toast.success('User unbanned successfully');
            setUnbanModalOpen(false);
            setUnbanReason('');
            dispatch(getPlayerProfile(playerId)); // Refresh profile
        } catch (error) {
            toast.error(error.message || 'Failed to unban user');
        }
    };

    const handleRevealPhone = async () => {
        try {
            const result = await dispatch(getPlayerFullPhone(playerId));
            setRevealedPhone(result.phoneNumber);
            toast.info('Phone number access logged for audit');
        } catch (error) {
            toast.error(error.message || 'Unable to reveal phone number');
        }
    };

    // Remarks Handler
    const handleSaveRemarks = () => {
        dispatch(updatePlayerRemarks(playerId, remarksText, remarksCategory));
    };

    const handleCancelRemarksEdit = () => {
        // Reset to the fetched values
        setRemarksText(playerRemarks?.current?.text || '');
        setRemarksCategory(playerRemarks?.current?.category || 'general');
        setRemarksEditMode(false);
    };

    const handleDeleteRemarks = async () => {
        if (await confirm({ title: 'Delete remarks', message: 'Are you sure you want to delete the remarks?', confirmText: 'Delete', danger: true })) {
            dispatch(updatePlayerRemarks(playerId, '', null));
        }
    };

    // Remarks category options
    const remarksCategoryOptions = [
        { value: 'general', label: 'General Note' },
        { value: 'fraud_suspected', label: 'Fraud Suspected' },
        { value: 'account_issue', label: 'Account Issue' },
        { value: 'verification_pending', label: 'Verification Pending' },
        { value: 'vip_player', label: 'VIP Player' },
        { value: 'compliance', label: 'Compliance' },
        { value: 'other', label: 'Other' },
    ];

    const getCategoryLabel = (category) => {
        const option = remarksCategoryOptions.find(opt => opt.value === category);
        return option ? option.label : category || 'General Note';
    };

    const getCategoryTone = (category) => {
        switch (category) {
            case 'fraud_suspected': return 'danger';
            case 'account_issue':
            case 'verification_pending': return 'warning';
            case 'vip_player': return 'gold';
            case 'compliance': return 'info';
            default: return 'neutral';
        }
    };

    const handleAssignClub = () => {
        if (!selectedClubsToAssign || selectedClubsToAssign.length === 0) {
            toast.error('Please select at least one club');
            return;
        }
        // Assign multiple clubs - now just one dispatch
        dispatch(assignPlayerClub(playerId, selectedClubsToAssign));
    };

    const handleToggleClubSelection = (clubId) => {
        setSelectedClubsToAssign(prev => {
            if (prev.includes(clubId)) {
                return prev.filter(id => id !== clubId);
            } else {
                return [...prev, clubId];
            }
        });
    };

    const handleRemoveClub = async (clubId, clubName) => {
        if (await confirm({ title: 'Remove club access', message: `Are you sure you want to remove access to ${clubName} from this player?`, confirmText: 'Remove', danger: true })) {
            dispatch(removePlayerClub(playerId, clubId));
        }
    };

    const handlePageChange = (newPage) => {
        setTransactionPage(newPage);
        dispatch(getPlayerTransactions(playerId, newPage, 10));
    };

    // Session Handlers
    const handleStartSession = () => {
        if (!selectedClubForSession) {
            toast.error('Please select a club');
            return;
        }
        dispatch(startPlayerSession(playerId, selectedClubForSession));
    };

    const handleEndSession = async () => {
        if (await confirm({ title: 'End session', message: 'Are you sure you want to end the current session?', confirmText: 'End session', danger: true })) {
            const currentClubId = activeSession?.clubId?._id || activeSession?.clubId;
            dispatch(endPlayerSession(playerId, currentClubId));
        }
    };

    const formatElapsedTime = (seconds) => {
        const h = Math.floor(seconds / 3600);
        const m = Math.floor((seconds % 3600) / 60);
        const s = seconds % 60;
        return `${h}h ${m}m ${s}s`;
    };

    // ============================================================================
    // MODAL CLOSE HANDLERS (reset form state)
    // ============================================================================

    const closeBanModal = () => {
        setBanModalOpen(false);
        setBanRemarks('');
        setBanCooldownDays(7);
    };

    const closeUnbanModal = () => {
        setUnbanModalOpen(false);
        setUnbanReason('');
    };

    const closeAssignClubModal = () => {
        setAssignClubModalOpen(false);
        setSelectedClubsToAssign([]);
    };

    const closeSessionHoursModal = () => {
        setSessionHoursModalOpen(false);
        setSessionHoursData({
            clubId: '',
            sessionDate: new Date().toISOString().split('T')[0],
            hoursPlayed: '',
            remarks: ''
        });
    };

    const handleLogSessionHours = () => {
        if (!sessionHoursData.clubId) {
            toast.error('Please select a club');
            return;
        }
        if (!sessionHoursData.hoursPlayed || parseFloat(sessionHoursData.hoursPlayed) <= 0) {
            toast.error('Please enter valid hours');
            return;
        }
        dispatch(logSessionHours({
            playerId,
            clubId: sessionHoursData.clubId,
            sessionDate: sessionHoursData.sessionDate,
            hoursPlayed: parseFloat(sessionHoursData.hoursPlayed),
            remarks: sessionHoursData.remarks
        }));
    };

    // ============================================================================
    // RENDER: Loading State
    // ============================================================================
    if (loadingProfile) {
        return <Loading />;
    }

    if (!player && errorProfile) {
        return (
            <Page>
                <BackLink onClick={() => navigate(-1)} />
                <Card>
                    <ErrorState
                        title="Unable to load player profile"
                        description={errorProfile}
                        onRetry={() => dispatch(getPlayerProfile(playerId))}
                    />
                </Card>
            </Page>
        );
    }

    if (!player) {
        return <Loading />;
    }

    // ============================================================================
    // RENDER: Derived values
    // ============================================================================
    const verification = player.verification || {};
    const manualKYC = verification.manualKYC || {};
    const isBanned = player.ban?.isBanned === true;
    const sessionStats = player.sessionStats;
    const avgSessionHours = sessionStats?.totalSessions > 0
        ? Math.round((sessionStats.totalHoursAllClubs / sessionStats.totalSessions) * 10) / 10
        : 0;
    const autoTrackedHours = (automaticHours || []).reduce((sum, c) => sum + (c.totalHours || 0), 0);
    const canBan = userInfo?.role?.playersFreeze;
    const canViewSensitive = userInfo?.role?.playersViewSensitive;

    const kycDocuments = [
        { label: 'PAN card', url: manualKYC.pan?.imageUrl },
        { label: 'Aadhaar front', url: manualKYC.aadhaar?.front?.imageUrl },
        { label: 'Aadhaar back', url: manualKYC.aadhaar?.back?.imageUrl },
    ].filter((doc) => doc.url);

    // Only the last four digits of an Aadhaar number are ever shown: XXXX XXXX 1234
    const formatMaskedAadhaar = (value) => {
        const lastFour = String(value || '').replace(/\D/g, '').slice(-4);
        return lastFour.length === 4 ? `XXXX XXXX ${lastFour}` : 'XXXX XXXX XXXX';
    };

    const kycRows = [
        {
            label: 'Aadhaar',
            verified: verification.aadhaar?.verified,
            detail: verification.aadhaar?.number
                ? <span className="font-mono">{formatMaskedAadhaar(verification.aadhaar.number)}</span>
                : manualKYC.aadhaar?.front?.imageUrl ? 'Document uploaded' : null,
        },
        {
            label: 'PAN',
            verified: verification.pan?.verified,
            detail: verification.pan?.number
                ? <span className="font-mono">{verification.pan.number}</span>
                : manualKYC.pan?.imageUrl ? 'Document uploaded' : null,
        },
        {
            label: 'Bank account',
            verified: verification.bank?.verified,
            detail: verification.bank?.accountNumber
                ? [
                    `${verification.bank.bankName || 'Bank'} · A/C ${verification.bank.accountNumber}`,
                    verification.bank.ifscCode && `IFSC ${verification.bank.ifscCode}`,
                ].filter(Boolean).join(' · ')
                : null,
        },
    ];

    const tabItems = [
        { value: 'overview', label: 'Overview', icon: LayoutDashboard },
        { value: 'transactions', label: 'Transactions', icon: ReceiptText, count: pagination?.totalCount },
        { value: 'clubs', label: 'Clubs', icon: Building2, count: clubs?.length },
        { value: 'affiliates', label: 'Affiliates', icon: Users },
        { value: 'admin', label: 'Admin', icon: ShieldAlert },
    ];

    // ============================================================================
    // RENDER: Transaction Table Columns
    // ============================================================================
    const referralBonusLabel = {
        granted: { tone: 'success', label: 'Bonus received' },
        pending: { tone: 'warning', label: 'Pending check-in' },
        'not-eligible': { tone: 'neutral', label: 'Not eligible' },
    };

    const referralColumns = [
        {
            title: 'Player',
            field: 'name',
            render: (rowData) => (
                <button
                    type="button"
                    onClick={() => navigate(`/players/${rowData._id}`)}
                    className="text-left hover:underline underline-offset-4"
                >
                    <PersonCell name={rowData.name || 'Unknown player'} sub={rowData.phoneNumber} />
                </button>
            ),
        },
        {
            title: 'Account created',
            field: 'createdAt',
            render: (rowData) => <span className="text-sm">{formatDate(rowData.createdAt)}</span>,
        },
        {
            title: 'Bonus',
            field: 'bonusStatus',
            render: (rowData) => {
                const bonus = referralBonusLabel[rowData.bonusStatus] || referralBonusLabel['not-eligible'];
                return (
                    <div className="flex flex-col items-start gap-0.5">
                        <StatusPill tone={bonus.tone} size="sm">{bonus.label}</StatusPill>
                        {rowData.bonusGrantedAt && (
                            <span className="text-xs text-cm-muted">{moment(rowData.bonusGrantedAt).format('DD MMM YYYY')}</span>
                        )}
                    </div>
                );
            },
        },
        {
            title: 'First check-in club',
            field: 'firstCheckIn.clubName',
            render: (rowData) => rowData.firstCheckIn ? (
                <div className="flex flex-col gap-0.5">
                    <span className="text-sm font-semibold text-cm-text">{rowData.firstCheckIn.clubName || 'Unknown club'}</span>
                    <span className="text-xs text-cm-muted">{formatDate(rowData.firstCheckIn.checkedInAt)}</span>
                </div>
            ) : (
                <span className="text-sm text-cm-muted">Not checked in yet</span>
            ),
        },
    ];

    const transactionColumns = [
        {
            title: 'Transaction ID',
            field: 'transactionId',
            render: (rowData) => (
                <span className="font-mono text-xs text-cm-muted">{displayTransactionId(rowData)}</span>
            ),
        },
        {
            title: 'Type',
            field: 'transactionType',
            render: (rowData) => <TransactionTypeBadge type={rowData.transactionType} />,
        },
        {
            title: 'Amount',
            field: 'amount',
            align: 'right',
            render: (rowData) => <Money value={rowData.amount} className="font-semibold" />,
        },
        {
            title: 'Balance after',
            field: 'balanceAfter',
            align: 'right',
            render: (rowData) => <Money value={rowData.balanceAfter} />,
        },
        {
            title: 'Status',
            field: 'status',
            render: (rowData) => (
                <StatusPill status={rowData.status} size="sm">
                    {rowData.status ? rowData.status.charAt(0).toUpperCase() + rowData.status.slice(1) : '—'}
                </StatusPill>
            ),
        },
        {
            title: 'Club',
            field: 'club.name',
            render: (rowData) => rowData.club?.name || '—',
        },
        {
            title: 'Date',
            field: 'createdAt',
            render: (rowData) => <span className="whitespace-nowrap">{formatDate(rowData.createdAt)}</span>,
        },
    ];

    // ============================================================================
    // RENDER: Main Component
    // ============================================================================
    return (
        <Page>
            <BackLink onClick={() => navigate(-1)} />

            {/* ================================================================ */}
            {/* IDENTITY */}
            {/* ================================================================ */}
            <Card padded={false}>
                <div className="flex flex-col gap-4 p-4 sm:p-5 md:flex-row md:items-center md:justify-between">
                    <div className="flex items-center gap-4 min-w-0">
                        <Avatar name={player.name || '?'} size={64} tone={isBanned ? 'red' : 'gold'} />
                        <div className="min-w-0">
                            <div className="flex flex-wrap items-center gap-2">
                                <h1 className="font-display text-xl sm:text-2xl font-extrabold text-cm-text m-0 leading-tight break-words">
                                    {player.name || 'Unknown player'}
                                </h1>
                                <StatusPill status={player.status ? 'Active' : 'Inactive'} size="sm" />
                                {isBanned && <StatusPill status="Banned" size="sm" />}
                            </div>
                            <div className="flex flex-wrap items-center gap-1.5 mt-2">
                                {player.identifier && (
                                    <Badge><span className="font-mono">{player.identifier}</span></Badge>
                                )}
                                <StatusPill
                                    tone={verification.isFullyVerified ? 'success' : 'warning'}
                                    icon={verification.isFullyVerified ? BadgeCheck : undefined}
                                    size="sm"
                                >
                                    {verification.isFullyVerified ? 'KYC verified' : 'KYC partial'}
                                </StatusPill>
                                {verification.verificationMethod === 'manual' && <Badge tone="info">Manual KYC</Badge>}
                            </div>
                        </div>
                    </div>
                    {canBan && (
                        <div className="flex flex-wrap gap-2">
                            {isBanned ? (
                                <Button variant="success" size="sm" icon={Unlock} onClick={() => setUnbanModalOpen(true)}>
                                    Unban
                                </Button>
                            ) : (
                                <Button variant="danger" size="sm" icon={Ban} onClick={() => setBanModalOpen(true)}>
                                    Ban player
                                </Button>
                            )}
                        </div>
                    )}
                </div>

                <dl className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 m-0 px-4 py-4 sm:px-5 border-t border-cm-border">
                    <KeyValue label="Phone">
                        <span className="inline-flex flex-wrap items-center gap-x-2">
                            <span className="tabular-nums">{revealedPhone || player.phoneNumber || '—'}</span>
                            {!revealedPhone && canViewSensitive && (
                                <button
                                    type="button"
                                    onClick={handleRevealPhone}
                                    className="inline-flex items-center gap-1 text-xs font-semibold text-cm-gold-text hover:underline"
                                    title="View full phone (logged for audit)"
                                >
                                    <Eye size={13} aria-hidden="true" />
                                    Reveal
                                </button>
                            )}
                        </span>
                    </KeyValue>
                    <KeyValue label="Email">{player.email || '—'}</KeyValue>
                    <KeyValue label="Joined">{player.joinedOn || formatDate(player.createdAt)}</KeyValue>
                    <KeyValue label="Last login">{player.lastLogin ? formatDate(player.lastLogin) : 'Never'}</KeyValue>
                    <KeyValue label="Referral code">
                        {player.referralCode ? <span className="font-mono">{player.referralCode}</span> : '—'}
                    </KeyValue>
                    <KeyValue label="Referred by">{player.referredBy?.name || '—'}</KeyValue>
                </dl>
            </Card>

            {/* Ban banner */}
            {isBanned && (
                <Notice
                    tone="danger"
                    icon={Ban}
                    title={`Banned · ${player.ban.remainingDays} day(s) left`}
                    action={
                        canBan && (
                            <Button variant="secondary" size="sm" onClick={() => setUnbanModalOpen(true)}>
                                Unban
                            </Button>
                        )
                    }
                >
                    {player.ban.remarks || 'No reason specified'} · Expires {moment(player.ban.banExpiresAt).format('DD MMM YYYY')}
                </Notice>
            )}

            {/* Live session banner */}
            {activeSession && (
                <Notice
                    tone="success"
                    icon={Timer}
                    title={`Live session at ${activeSession.clubName || 'Unknown club'}`}
                    action={
                        <Button variant="danger" size="sm" icon={Square} loading={loadingEndSession} onClick={handleEndSession}>
                            End session
                        </Button>
                    }
                >
                    Started {moment(activeSession.sessionStart).format('hh:mm A')} ·{' '}
                    <span className="font-semibold tabular-nums">{formatElapsedTime(elapsedTime)}</span>
                </Notice>
            )}

            {/* ================================================================ */}
            {/* FINANCIAL KPIs */}
            {/* ================================================================ */}
            <StatGrid>
                <StatCard
                    label="Wallet balance"
                    icon={Wallet}
                    tone="gold"
                    loading={loadingSummary}
                    value={summary ? formatCurrency(summary.walletBalance) : '—'}
                    hint={summary ? `${summary.transactionCount ?? 0} transactions` : undefined}
                />
                <StatCard
                    label="Total top-ups"
                    icon={ArrowDownLeft}
                    loading={loadingSummary}
                    value={summary ? formatCurrency(summary.totalDeposits) : '—'}
                    hint={summary ? `Avg ${formatCurrency(summary.avgTransactionAmount)} per transaction` : undefined}
                />
                <StatCard
                    label="Total spend"
                    icon={ShoppingBag}
                    loading={loadingSummary}
                    value={summary ? formatCurrency(summary.totalSpend) : '—'}
                    hint={summary ? 'Food, table charges, memberships & events' : undefined}
                />
                <StatCard
                    label="Unpaid transactions"
                    icon={FileWarning}
                    tone={summary?.unpaidTransactions > 0 ? 'warning' : undefined}
                    loading={loadingSummary}
                    value={summary ? summary.unpaidTransactions : '—'}
                    hint={summary ? `${formatCurrency(summary.unpaidAmount)} outstanding` : undefined}
                />
            </StatGrid>

            {/* ================================================================ */}
            {/* TAB NAVIGATION */}
            {/* ================================================================ */}
            <Tabs items={tabItems} value={activeTab} onChange={setActiveTab} ariaLabel="Player profile sections" />

            {/* ================================================================ */}
            {/* TAB: OVERVIEW */}
            {/* ================================================================ */}
            {activeTab === 'overview' && (
                <div role="tabpanel" className="grid gap-5 lg:grid-cols-3 items-start">
                    <div className="flex flex-col gap-5 lg:col-span-2 min-w-0">
                        {/* Activity Analytics */}
                        <Card>
                            <CardHeader
                                title="Activity"
                                description={analytics?.period || 'Last 30 days'}
                            />
                            {loadingAnalytics ? (
                                <TableSkeleton rows={2} cols={3} />
                            ) : analytics ? (
                                <dl className="grid grid-cols-2 sm:grid-cols-3 gap-4 m-0">
                                    <KeyValue label="Transactions">
                                        <span className="text-lg font-bold tabular-nums">{analytics.transactionsInPeriod}</span>
                                    </KeyValue>
                                    <KeyValue label="Total amount">
                                        <span className="text-lg font-bold tabular-nums">{formatCurrency(analytics.totalAmountInPeriod)}</span>
                                    </KeyValue>
                                    <KeyValue label="Avg transaction">
                                        <span className="text-lg font-bold tabular-nums">{formatCurrency(analytics.averageTransaction)}</span>
                                    </KeyValue>
                                    <KeyValue label="Frequency">{analytics.transactionFrequency}</KeyValue>
                                    <KeyValue label="Most active day">{analytics.mostActiveDay || '—'}</KeyValue>
                                    <KeyValue label="Account age">{analytics.accountAge?.formatted || '—'}</KeyValue>
                                    {analytics.favoriteClub && (
                                        <KeyValue label="Favourite club" className="col-span-2 sm:col-span-3">
                                            {analytics.favoriteClub.name}
                                            <span className="text-cm-muted"> · {analytics.favoriteClub.transactions} transactions</span>
                                        </KeyValue>
                                    )}
                                </dl>
                            ) : (
                                <EmptyState compact icon={TrendingUp} title="No activity yet" description="Analytics appear once the player transacts." />
                            )}
                        </Card>

                        {/* Club Session Hours */}
                        {sessionStats && (
                            <Card>
                                <CardHeader title="Club session hours" description="All-time, across every club" />
                                <dl className="grid grid-cols-3 gap-4 m-0 mb-5">
                                    <KeyValue label="Total hours">
                                        <span className="text-lg font-bold tabular-nums">{sessionStats.totalHoursAllClubs || 0}h</span>
                                    </KeyValue>
                                    <KeyValue label="Sessions">
                                        <span className="text-lg font-bold tabular-nums">{sessionStats.totalSessions || 0}</span>
                                    </KeyValue>
                                    <KeyValue label="Avg per session">
                                        <span className="text-lg font-bold tabular-nums">{avgSessionHours}h</span>
                                    </KeyValue>
                                </dl>
                                {!activeSession && !loadingActiveSession && (
                                    <p className="text-xs text-cm-muted m-0 mb-4">
                                        No active session. Sessions are started from Dealer Management.
                                    </p>
                                )}
                                {sessionStats.clubsTimeSpent?.length > 0 ? (
                                    <ul className="grid gap-3 sm:grid-cols-2 m-0 p-0 list-none">
                                        {sessionStats.clubsTimeSpent.map((club, index) => (
                                            <li
                                                key={club.clubId || index}
                                                className="rounded-cm-lg border border-cm-border bg-cm-surface-2 p-3"
                                            >
                                                <div className="flex items-center justify-between gap-2 mb-2">
                                                    <p className="text-sm font-semibold text-cm-text m-0 truncate">{club.clubName}</p>
                                                    {club.clubCode && <Badge><span className="font-mono">{club.clubCode}</span></Badge>}
                                                </div>
                                                <p className="text-lg font-bold text-cm-text m-0 tabular-nums">{club.totalHours}h</p>
                                                <p className="text-xs text-cm-muted m-0 mt-0.5">
                                                    {club.sessionCount} sessions · avg {club.averageSessionMinutes} min
                                                    {club.lastSession && ` · last ${moment(club.lastSession).format('DD MMM YYYY')}`}
                                                </p>
                                            </li>
                                        ))}
                                    </ul>
                                ) : (
                                    <EmptyState
                                        compact
                                        icon={Timer}
                                        title="No sessions yet"
                                        description="The player hasn't completed any club sessions."
                                    />
                                )}
                            </Card>
                        )}
                    </div>

                    <div className="flex flex-col gap-5 min-w-0">
                        {/* KYC Verification */}
                        <Card>
                            <CardHeader
                                title="Verification"
                                description={`Current step: ${verification.verificationStep || 'N/A'}`}
                            />
                            <ul className="flex flex-col divide-y divide-cm-border m-0 p-0 list-none">
                                {kycRows.map((row) => (
                                    <li key={row.label} className="flex items-center justify-between gap-3 py-2.5 first:pt-0 last:pb-0">
                                        <div className="min-w-0">
                                            <p className="text-sm font-semibold text-cm-text m-0">{row.label}</p>
                                            {row.detail && <p className="text-xs text-cm-muted m-0 break-words">{row.detail}</p>}
                                        </div>
                                        <StatusPill tone={row.verified ? 'success' : 'warning'} size="sm">
                                            {row.verified ? 'Verified' : 'Pending'}
                                        </StatusPill>
                                    </li>
                                ))}
                            </ul>
                            {manualKYC.status === 'approved' && kycDocuments.length > 0 && (
                                <div className="mt-4 pt-4 border-t border-cm-border">
                                    <p className="text-xs font-semibold text-cm-muted m-0 mb-2">Manual KYC documents</p>
                                    <div className="grid grid-cols-3 gap-2">
                                        {kycDocuments.map((doc) => (
                                            <a
                                                key={doc.label}
                                                href={resolveUploadUrl(doc.url)}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="block rounded-cm border border-cm-border bg-cm-surface-2 p-1 hover:border-cm-border-strong"
                                            >
                                                <img
                                                    src={resolveUploadUrl(doc.url)}
                                                    alt={doc.label}
                                                    className="w-full h-16 object-contain"
                                                    onError={(event) => handleUploadImageError(event, doc.url)}
                                                />
                                                <span className="block text-[11px] text-cm-muted text-center mt-1 truncate">{doc.label}</span>
                                            </a>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </Card>

                        {/* Admin Remarks */}
                        <Card>
                            <CardHeader
                                title="Admin remarks"
                                description="Internal notes. Changes are logged."
                                actions={
                                    !remarksEditMode && playerRemarks?.current?.text && (
                                        <>
                                            <IconButton icon={Pencil} label="Edit remarks" variant="ghost" size="sm" onClick={() => setRemarksEditMode(true)} />
                                            <IconButton icon={Trash2} label="Delete remarks" variant="ghost" size="sm" onClick={handleDeleteRemarks} />
                                        </>
                                    )
                                }
                            />
                            {loadingRemarks ? (
                                <TableSkeleton rows={2} cols={1} />
                            ) : remarksEditMode ? (
                                <div className="flex flex-col gap-3">
                                    <SelectInput
                                        label="Category"
                                        value={remarksCategory}
                                        onChange={(e) => setRemarksCategory(e.target.value)}
                                        options={remarksCategoryOptions}
                                    />
                                    <Textarea
                                        label="Remarks"
                                        value={remarksText}
                                        onChange={(e) => setRemarksText(e.target.value)}
                                        rows={4}
                                        placeholder="Enter remarks about this player…"
                                    />
                                    <div className="flex justify-end gap-2">
                                        <Button variant="secondary" size="sm" onClick={handleCancelRemarksEdit}>
                                            Cancel
                                        </Button>
                                        <Button size="sm" loading={loadingUpdateRemarks} onClick={handleSaveRemarks}>
                                            Save remarks
                                        </Button>
                                    </div>
                                </div>
                            ) : playerRemarks?.current?.text ? (
                                <div>
                                    <Badge tone={getCategoryTone(playerRemarks.current.category)}>
                                        {getCategoryLabel(playerRemarks.current.category)}
                                    </Badge>
                                    <p className="text-sm text-cm-text whitespace-pre-wrap mt-2 mb-0">{playerRemarks.current.text}</p>
                                    <p className="text-xs text-cm-muted mt-2 mb-0">
                                        {playerRemarks.current.updatedBy?.name || playerRemarks.current.updatedBy?.identifier || 'Unknown'}
                                        {playerRemarks.current.updatedAt && ` · ${moment(playerRemarks.current.updatedAt).format('DD MMM YYYY, HH:mm')}`}
                                    </p>
                                </div>
                            ) : (
                                <EmptyState
                                    compact
                                    icon={StickyNote}
                                    title="No remarks"
                                    action={
                                        <Button variant="secondary" size="sm" icon={Plus} onClick={() => setRemarksEditMode(true)}>
                                            Add remarks
                                        </Button>
                                    }
                                />
                            )}

                            {playerRemarks?.history?.length > 0 && (
                                <div className="mt-4 pt-3 border-t border-cm-border">
                                    <button
                                        type="button"
                                        onClick={() => setShowRemarksHistory(!showRemarksHistory)}
                                        className="inline-flex items-center gap-1 text-xs font-semibold text-cm-muted hover:text-cm-text"
                                        aria-expanded={showRemarksHistory}
                                    >
                                        <ChevronDown
                                            size={14}
                                            className={`transition-transform ${showRemarksHistory ? '' : '-rotate-90'}`}
                                            aria-hidden="true"
                                        />
                                        History ({playerRemarks.history.length})
                                    </button>
                                    {showRemarksHistory && (
                                        <ul className="flex flex-col gap-2 mt-3 m-0 p-0 list-none max-h-60 overflow-y-auto">
                                            {playerRemarks.history.slice().reverse().map((entry, idx) => (
                                                <li key={idx} className="rounded-cm bg-cm-surface-2 p-2.5 text-sm">
                                                    <div className="flex items-center justify-between gap-2 mb-1">
                                                        <Badge tone={entry.action === 'deleted' ? 'danger' : 'info'}>
                                                            {entry.action ? entry.action.charAt(0).toUpperCase() + entry.action.slice(1) : 'Updated'}
                                                        </Badge>
                                                        <span className="text-xs text-cm-muted">
                                                            {moment(entry.updatedAt).format('DD MMM YYYY, HH:mm')}
                                                        </span>
                                                    </div>
                                                    <p className="text-cm-text m-0 line-clamp-2">{entry.text || '(No text)'}</p>
                                                    <p className="text-xs text-cm-muted m-0 mt-1">
                                                        {entry.updatedBy?.name || entry.updatedBy?.identifier || 'Unknown'}
                                                    </p>
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </div>
                            )}
                        </Card>
                    </div>
                </div>
            )}

            {/* ================================================================ */}
            {/* TAB: TRANSACTIONS */}
            {/* ================================================================ */}
            {activeTab === 'transactions' && (
                <div role="tabpanel">
                    <DataTable
                        title="Transaction history"
                        columns={transactionColumns}
                        data={transactions || []}
                        loading={loadingTransactions}
                        search={false}
                        sortable={false}
                        pageSize={10}
                        total={pagination?.totalCount ?? (transactions || []).length}
                        page={transactionPage - 1}
                        onPageChange={(p) => handlePageChange(p + 1)}
                        emptyTitle="No transactions yet"
                        emptyDescription="Top-ups, buy-ins and cashouts will appear here."
                    />
                </div>
            )}

            {/* ================================================================ */}
            {/* TAB: CLUBS */}
            {/* ================================================================ */}
            {activeTab === 'clubs' && (
                <Card role="tabpanel">
                    <CardHeader
                        title="Clubs"
                        description="From transaction history and manual assignments."
                        actions={
                            <Button size="sm" icon={Plus} onClick={() => setAssignClubModalOpen(true)}>
                                Assign access
                            </Button>
                        }
                    />
                    {loadingClubs ? (
                        <TableSkeleton rows={3} cols={3} />
                    ) : clubs && clubs.length > 0 ? (
                        <ul className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3 m-0 p-0 list-none">
                            {clubs.map((club, index) => (
                                <li
                                    key={club._id || index}
                                    className={`flex flex-col gap-3 rounded-cm-lg border p-3 ${club.isAssigned
                                        ? 'border-cm-gold bg-cm-gold-soft'
                                        : 'border-cm-border bg-cm-surface-2'
                                        }`}
                                >
                                    <div className="flex items-center gap-3 min-w-0">
                                        <Avatar name={club.name || 'Club'} size={36} />
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm font-semibold text-cm-text m-0 truncate">{club.name}</p>
                                            <p className="text-xs text-cm-muted font-mono m-0">{club.clubCode}</p>
                                        </div>
                                        {club.isAssigned && <Badge tone="gold">Assigned</Badge>}
                                    </div>
                                    <dl className="grid grid-cols-3 gap-2 m-0">
                                        <KeyValue label="Transactions">{club.transactionCount}</KeyValue>
                                        <KeyValue label="Total"><Money value={club.totalAmount} /></KeyValue>
                                        <KeyValue label="Last activity">
                                            {club.lastTransaction ? moment(club.lastTransaction).format('DD MMM YY') : 'None'}
                                        </KeyValue>
                                    </dl>
                                    {club.isAssigned && (
                                        <div className="flex justify-end">
                                            <Button
                                                variant="ghost"
                                                size="xs"
                                                icon={Trash2}
                                                disabled={loadingRemoveClub}
                                                onClick={() => handleRemoveClub(club._id, club.name)}
                                            >
                                                Remove access
                                            </Button>
                                        </div>
                                    )}
                                </li>
                            ))}
                        </ul>
                    ) : (
                        <EmptyState
                            compact
                            icon={Building2}
                            title="No club activity"
                            description="The player hasn't transacted at any club yet. You can assign access manually."
                        />
                    )}
                </Card>
            )}

            {/* ================================================================ */}
            {/* TAB: AFFILIATES */}
            {/* ================================================================ */}
            {activeTab === 'affiliates' && (
                <div role="tabpanel" className="flex flex-col gap-5">
                    <Card>
                        <CardHeader
                            title="Session hours"
                            description="Used to calculate affiliate commission."
                            actions={
                                <Button variant="secondary" size="sm" icon={CalendarClock} onClick={() => setSessionHoursModalOpen(true)}>
                                    Log hours
                                </Button>
                            }
                        />
                        <div className="grid grid-cols-1 sm:grid-cols-2 divide-y sm:divide-y-0 sm:divide-x divide-cm-border">
                            <div className="pb-4 sm:pb-0 sm:pr-5">
                                <p className="text-xs font-semibold text-cm-muted m-0">Manual (this month)</p>
                                <p className="text-2xl font-bold text-cm-text m-0 tabular-nums">{manualHours?.totalHours || 0} hrs</p>
                                {manualHours?.byClub?.map((club, idx) => (
                                    <p key={idx} className="text-xs text-cm-muted m-0">{club.clubName}: {club.totalHours} hrs</p>
                                ))}
                            </div>
                            <div className="pt-4 sm:pt-0 sm:pl-5">
                                <p className="text-xs font-semibold text-cm-muted m-0">Auto-tracked (all time)</p>
                                <p className="text-2xl font-bold text-cm-text m-0 tabular-nums">{autoTrackedHours.toFixed(1)} hrs</p>
                                {automaticHours?.slice(0, 3).map((club, idx) => (
                                    <p key={idx} className="text-xs text-cm-muted m-0">{club.clubName}: {club.totalHours} hrs</p>
                                ))}
                            </div>
                        </div>
                    </Card>

                    <DataTable
                        title={`Referred players (${referrals.length})`}
                        description={player?.referralCode
                            ? `Signed up with referral code ${player.referralCode} · ${bonusGrantedCount} received a bonus`
                            : 'This player has no referral code yet.'}
                        columns={referralColumns}
                        data={referrals}
                        loading={loadingReferrals}
                        search={false}
                        sortable={false}
                        pageSize={10}
                        emptyTitle="No referred players"
                        emptyDescription="Players who sign up with this player's referral code appear here."
                    />
                </div>
            )}

            {/* ================================================================ */}
            {/* TAB: ADMIN */}
            {/* ================================================================ */}
            {activeTab === 'admin' && (
                <div role="tabpanel" className="flex flex-col gap-5">
                    <Notice tone="warning" icon={ShieldAlert}>
                        Every action here is logged and auditable.
                    </Notice>
                    <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                        {canBan && (
                            <Card>
                                <div className="flex items-start gap-3">
                                    <IconChip icon={isBanned ? Lock : Ban} tone={isBanned ? 'danger' : 'neutral'} />
                                    <div className="min-w-0 flex-1">
                                        <h3 className="text-base font-bold text-cm-text m-0">
                                            {isBanned ? 'Player is banned' : 'Ban player'}
                                        </h3>
                                        {isBanned ? (
                                            <dl className="grid gap-2 m-0 mt-2">
                                                <KeyValue label="Reason">{player.ban.remarks || 'No reason specified'}</KeyValue>
                                                <KeyValue label="Remaining">{player.ban.remainingDays} day(s)</KeyValue>
                                                <KeyValue label="Expires">{moment(player.ban.banExpiresAt).format('DD MMM YYYY')}</KeyValue>
                                            </dl>
                                        ) : (
                                            <p className="text-sm text-cm-muted mt-1 mb-0">
                                                Block login for a cooldown period. The player sees your remarks when they try to log in.
                                            </p>
                                        )}
                                        <div className="mt-4">
                                            {isBanned ? (
                                                <Button variant="success" size="sm" icon={Unlock} onClick={() => setUnbanModalOpen(true)}>
                                                    Unban player
                                                </Button>
                                            ) : (
                                                <Button variant="danger" size="sm" icon={Ban} onClick={() => setBanModalOpen(true)}>
                                                    Ban player
                                                </Button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </Card>
                        )}

                        {canViewSensitive && (
                            <Card>
                                <div className="flex items-start gap-3">
                                    <IconChip icon={Eye} tone="info" />
                                    <div className="min-w-0 flex-1">
                                        <h3 className="text-base font-bold text-cm-text m-0">Full phone number</h3>
                                        <p className="text-sm text-cm-muted mt-1 mb-0">
                                            Reveal the unmasked number. Access is logged for compliance.
                                        </p>
                                        <div className="mt-4">
                                            {revealedPhone ? (
                                                <span className="inline-block font-mono text-sm rounded-cm bg-cm-success-soft text-cm-success px-2.5 py-1.5">
                                                    {revealedPhone}
                                                </span>
                                            ) : (
                                                <Button variant="secondary" size="sm" icon={Eye} onClick={handleRevealPhone}>
                                                    Reveal number
                                                </Button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </Card>
                        )}

                        <Card>
                            <div className="flex items-start gap-3">
                                <IconChip icon={Download} tone="neutral" />
                                <div className="min-w-0 flex-1">
                                    <h3 className="text-base font-bold text-cm-text m-0">Export player data</h3>
                                    <p className="text-sm text-cm-muted mt-1 mb-0">
                                        Download all player data for compliance or support requests.
                                    </p>
                                    <div className="mt-4">
                                        <Button variant="secondary" size="sm" icon={Download} disabled>
                                            Coming soon
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        </Card>
                    </div>
                </div>
            )}

            {/* ================================================================ */}
            {/* FREEZE MODAL */}
            {/* ================================================================ */}
            <Modal
                open={freezeModalOpen}
                onClose={() => setFreezeModalOpen(false)}
                title={player.status ? 'Freeze player account' : 'Unfreeze player account'}
                description={player.status
                    ? 'This will prevent the player from making any transactions until unfrozen.'
                    : 'This will allow the player to resume making transactions.'}
                icon={player.status ? Lock : Unlock}
                tone={player.status ? 'danger' : 'success'}
                footer={
                    <>
                        <Button variant="secondary" onClick={() => setFreezeModalOpen(false)}>Cancel</Button>
                        <Button
                            variant={player.status ? 'danger' : 'success'}
                            loading={loadingFreeze}
                            onClick={handleFreezeToggle}
                        >
                            {player.status ? 'Freeze account' : 'Unfreeze account'}
                        </Button>
                    </>
                }
            >
                <Textarea
                    label="Reason (optional)"
                    value={freezeReason}
                    onChange={(e) => setFreezeReason(e.target.value)}
                    rows={3}
                    placeholder="Enter reason for this action…"
                />
            </Modal>

            {/* ================================================================ */}
            {/* BAN USER MODAL */}
            {/* ================================================================ */}
            <Modal
                open={banModalOpen}
                onClose={closeBanModal}
                title="Ban player"
                description="The player can't log in until the cooldown ends. They'll see your remarks when they try."
                icon={Ban}
                tone="danger"
                footer={
                    <>
                        <Button variant="secondary" onClick={closeBanModal}>Cancel</Button>
                        <Button
                            variant="danger"
                            onClick={handleBanUser}
                            disabled={!banRemarks.trim() || banCooldownDays < 1}
                        >
                            Ban player
                        </Button>
                    </>
                }
            >
                <div className="flex flex-col gap-4">
                    <TextInput
                        label="Cooldown period"
                        required
                        type="number"
                        suffix="days"
                        value={banCooldownDays}
                        onChange={(e) => setBanCooldownDays(parseInt(e.target.value) || 1)}
                        min={1}
                        max={365}
                        placeholder="7"
                    />
                    <Textarea
                        label="Ban remarks (shown to the player)"
                        required
                        value={banRemarks}
                        onChange={(e) => setBanRemarks(e.target.value)}
                        rows={3}
                        placeholder="Reason for this ban…"
                    />
                </div>
            </Modal>

            {/* ================================================================ */}
            {/* UNBAN USER MODAL */}
            {/* ================================================================ */}
            <Modal
                open={unbanModalOpen}
                onClose={closeUnbanModal}
                title="Unban player"
                description="Removes the ban so the player can log in and use the app again."
                icon={Unlock}
                tone="success"
                footer={
                    <>
                        <Button variant="secondary" onClick={closeUnbanModal}>Cancel</Button>
                        <Button variant="success" onClick={handleUnbanUser}>Unban player</Button>
                    </>
                }
            >
                <Textarea
                    label="Reason for unbanning (optional)"
                    value={unbanReason}
                    onChange={(e) => setUnbanReason(e.target.value)}
                    rows={3}
                    placeholder="Reason for unbanning…"
                />
            </Modal>

            {/* SESSION MODAL REMOVED - Sessions are started through Dealer Management */}
            {/* ================================================================ */}
            {/* ASSIGN CLUB MODAL */}
            {/* ================================================================ */}
            <Modal
                open={assignClubModalOpen}
                onClose={closeAssignClubModal}
                title="Assign club access"
                description="The player will appear in each selected club's member list."
                icon={Building2}
                footer={
                    <>
                        <span className="mr-auto self-center text-sm text-cm-muted">
                            {selectedClubsToAssign.length} selected
                        </span>
                        <Button variant="secondary" onClick={closeAssignClubModal}>Cancel</Button>
                        <Button
                            icon={Plus}
                            loading={loadingAssignClub}
                            disabled={selectedClubsToAssign.length === 0}
                            onClick={handleAssignClub}
                        >
                            Assign
                        </Button>
                    </>
                }
            >
                {allClubs && allClubs.length > 0 ? (
                    <div className="flex flex-col gap-2">
                        {allClubs.map((club) => {
                            const isAlreadyAssigned = clubs?.some(c => c._id === club._id && c.isAssigned);
                            const checked = selectedClubsToAssign.includes(club._id);
                            return (
                                <label
                                    key={club._id}
                                    className={`flex items-center gap-3 rounded-cm-lg border p-3 transition-colors ${checked
                                        ? 'border-cm-gold bg-cm-gold-soft cursor-pointer'
                                        : isAlreadyAssigned
                                            ? 'border-cm-border bg-cm-surface-2 opacity-60 cursor-not-allowed'
                                            : 'border-cm-border bg-cm-surface-2 hover:border-cm-border-strong cursor-pointer'
                                        }`}
                                >
                                    <input
                                        type="checkbox"
                                        name="clubToAssign"
                                        value={club._id}
                                        checked={checked}
                                        onChange={() => handleToggleClubSelection(club._id)}
                                        disabled={isAlreadyAssigned}
                                        className="w-4 h-4 accent-[var(--cm-gold)]"
                                    />
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm font-semibold text-cm-text m-0 truncate">{club.name}</p>
                                        <p className="text-xs text-cm-muted font-mono m-0">{club.clubCode}</p>
                                    </div>
                                    {isAlreadyAssigned && <Badge tone="success">Assigned</Badge>}
                                </label>
                            );
                        })}
                    </div>
                ) : (
                    <EmptyState compact icon={Building2} title="No clubs available" />
                )}
            </Modal>

            {/* ================================================================ */}
            {/* LOG SESSION HOURS MODAL */}
            {/* ================================================================ */}
            <Modal
                open={sessionHoursModalOpen}
                onClose={closeSessionHoursModal}
                title="Log session hours"
                description="Hours logged here count towards affiliate commission."
                icon={CalendarClock}
                footer={
                    <>
                        <Button variant="secondary" onClick={closeSessionHoursModal}>Cancel</Button>
                        <Button
                            loading={loadingLogSessionHours}
                            disabled={!sessionHoursData.clubId || !sessionHoursData.hoursPlayed}
                            onClick={handleLogSessionHours}
                        >
                            Log hours
                        </Button>
                    </>
                }
            >
                <div className="flex flex-col gap-4">
                    <SelectInput
                        label="Club"
                        required
                        value={sessionHoursData.clubId}
                        onChange={(e) => setSessionHoursData(prev => ({ ...prev, clubId: e.target.value }))}
                        placeholder="Select club"
                        options={(allClubs || []).map((club) => ({
                            value: club._id,
                            label: `${club.name} (${club.clubCode})`,
                        }))}
                    />
                    <div className="grid gap-4 sm:grid-cols-2">
                        <TextInput
                            label="Session date"
                            required
                            type="date"
                            value={sessionHoursData.sessionDate}
                            onChange={(e) => setSessionHoursData(prev => ({ ...prev, sessionDate: e.target.value }))}
                        />
                        <TextInput
                            label="Hours played"
                            required
                            type="number"
                            step="0.5"
                            min="0"
                            max="24"
                            suffix="hrs"
                            value={sessionHoursData.hoursPlayed}
                            onChange={(e) => setSessionHoursData(prev => ({ ...prev, hoursPlayed: e.target.value }))}
                            placeholder="e.g. 4.5"
                        />
                    </div>
                    <Textarea
                        label="Remarks (optional)"
                        value={sessionHoursData.remarks}
                        onChange={(e) => setSessionHoursData(prev => ({ ...prev, remarks: e.target.value }))}
                        rows={2}
                        placeholder="Any notes about this session…"
                    />
                </div>
            </Modal>
        </Page>
    );
};

export default PlayerProfile;
